/**
 * PermissionsContext — runtime enforcement of the page-permission system.
 *
 *   isSuper            → username 'admin' / is_super_admin → everything allowed, nothing hidden
 *   canAccess(section) → "الدخول" على الزر الرئيسي في لوحة التحكم
 *   canView(pageId)    → عرض الصفحة/الزر الفرعي (requires section access too)
 *   canEdit / canDelete(pageId)
 *   visiblePages(list, key) → filter helper for hub card arrays
 *
 * Fresh permissions are pulled from /api/auth/me on mount so role changes apply at next load.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { PAGE_INDEX, SECTION_OF_PAGE, SECTIONS, matchPage, ALWAYS_ALLOWED } from './registry';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const Ctx = createContext(null);

const isSuperUser = (u) => Boolean(u?.is_super_admin) || (u?.username || '').toLowerCase() === 'admin';

export function PermissionsProvider({ children }) {
  const { user, token, updateUser } = useAuth();
  const refreshed = useRef(false);

  useEffect(() => {
    if (!token) return undefined;
    const KEYS = ['is_super_admin', 'job_title_id', 'job_title_ar', 'job_title', 'permissions', 'platform_access'];
    const refresh = () => axios.get(`${BACKEND_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.data?.id) return;
        // only touch auth state when permission-relevant fields actually changed (a new user object re-renders the whole app)
        updateUser((prev) => {
          const changed = KEYS.some((k) => JSON.stringify(prev?.[k] ?? null) !== JSON.stringify(r.data[k] ?? null));
          if (!changed) return prev;
          const next = { ...(prev || {}) };
          KEYS.forEach((k) => { next[k] = r.data[k]; });
          return next;
        });
      })
      .catch(() => {});
    if (!refreshed.current) { refreshed.current = true; refresh(); }
    const id = setInterval(refresh, 180000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const value = useMemo(() => {
    const isSuper = isSuperUser(user);
    const perms = user?.permissions || {};
    const access = user?.platform_access || {};
    const canAccess = (sectionKey) => isSuper || Boolean(access[sectionKey]);
    const canView = (pageId) => {
      if (isSuper) return true;
      if (!pageId || !PAGE_INDEX[pageId]) return true; // unknown page → not governed
      return canAccess(SECTION_OF_PAGE[pageId]) && Boolean(perms[pageId]?.view);
    };
    const canEdit = (pageId) => isSuper || (!PAGE_INDEX[pageId] ? true : canView(pageId) && Boolean(perms[pageId]?.edit));
    const canDelete = (pageId) => isSuper || (!PAGE_INDEX[pageId] ? true : canView(pageId) && Boolean(perms[pageId]?.delete));
    const canViewRoute = (path) => { const n = matchPage(path.split('?')[0], path.includes('?') ? path.slice(path.indexOf('?')) : ''); return n ? canView(n.id) : true; };
    const visiblePages = (list, key = 'route') => list.filter((c) => canViewRoute(String(c[key] || '')));
    const sectionHasAnyVisible = (sectionKey) => isSuper || (canAccess(sectionKey) && (SECTIONS.find((s) => s.key === sectionKey)?.pages || []).some((p) => canView(p.id)));
    return { isSuper, user, canAccess, canView, canEdit, canDelete, canViewRoute, visiblePages, sectionHasAnyVisible };
  }, [user]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const usePerms = () => useContext(Ctx) || {
  isSuper: true, canAccess: () => true, canView: () => true, canEdit: () => true, canDelete: () => true,
  canViewRoute: () => true, visiblePages: (l) => l, sectionHasAnyVisible: () => true,
};

/* <Can page="/route" [action="view|edit|delete"]>…</Can> — renders children only when allowed. */
export function Can({ page, section, action = 'view', children, fallback = null }) {
  const p = usePerms();
  let ok = true;
  if (section) ok = p.canAccess(section);
  else if (page) ok = action === 'edit' ? p.canEdit(page) : action === 'delete' ? p.canDelete(page) : p.canView(page);
  return ok ? children : fallback;
}

export const isAlwaysAllowed = (pathname) => ALWAYS_ALLOWED.includes(pathname);
export const useCan = () => usePerms();
